import { Link, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { Check, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { ContactButton, CONTACT_EMAIL } from "./ContactButton";
import { useToast } from "@/hooks/use-toast";
import { SUBSCRIBE_INTENT_KEY, useStartCheckout, checkoutErrorToast } from "@/lib/billing";

type Plan = {
  name: string;
  price: string;
  period?: string;
  desc: string;
  features: string[];
  cta: string;
  href: string;
  external?: boolean;
  action?: "checkout";
  highlighted: boolean;
  note?: string;
};

const CLINIC_INQUIRY_SUBJECT = "PalNote Clinic Inquiry";
const CLINIC_INQUIRY_BODY = `Hello PalNote Team,

I'm interested in learning more about PalNote for my clinic.

Clinic Name:

Number of Providers:

Location:

Questions:

Thank you.`;

const plans: Plan[] = [
  {
    name: "Free Trial",
    price: "Free",
    desc: "Everything you need to evaluate PalNote before subscribing.",
    features: [
      "No credit card required",
      "Up to 25 AI SOAP notes",
      "Up to 25 voice recordings",
      "Up to 25 patient profiles",
      "AI documentation suggestions",
      "PDF export",
      "Full feature access",
      "Cancel anytime",
    ],
    cta: "Start 14-Day Free Trial",
    href: "/sign-up",
    highlighted: false,
    note: "No credit card required.",
  },
  {
    name: "Professional",
    price: "$79",
    period: "CAD / provider / month",
    desc: "Designed for independent chiropractors and healthcare providers.",
    features: [
      "Unlimited AI SOAP notes",
      "Unlimited voice recordings",
      "Unlimited patients",
      "AI Documentation Assistant",
      "Smart documentation suggestions",
      "PDF export",
      "Copy to Clipboard",
      "Priority AI processing",
      "Email support",
    ],
    cta: "Start Professional",
    href: "/sign-up",
    action: "checkout",
    highlighted: true,
  },
  {
    name: "Clinic",
    price: "Custom Pricing",
    desc: "Rehabilitation clinic software designed for teams with multiple providers.",
    features: [
      "Everything in Professional",
      "Individual login for every provider",
      "Multi-provider management",
      "Shared clinic administration",
      "Shared patient database with proper permissions",
      "Role-based access (Admin, Provider, Staff)",
      "Centralized billing",
      "Team management",
      "Priority onboarding",
      "Priority support",
    ],
    cta: "Contact Sales",
    href: `mailto:${CONTACT_EMAIL}`,
    external: true,
    highlighted: false,
  },
];

const guarantees = [
  "No long-term contracts",
  "Cancel anytime",
  "Secure cloud storage",
  "Designed for chiropractors & MSK clinics",
];

// Renders identically to the previous plan CTA link, but starts Stripe Checkout
// for signed-in users (or routes to sign-up with a resume-intent otherwise).
function ProCheckoutButton({
  className,
  fallbackHref,
  children,
}: {
  className: string;
  fallbackHref: string;
  children: React.ReactNode;
}) {
  const { isSignedIn } = useAuth();
  const [, setLocation] = useLocation();
  const { start, isPending } = useStartCheckout();
  const { toast } = useToast();

  const onClick = async () => {
    if (!isSignedIn) {
      localStorage.setItem(SUBSCRIBE_INTENT_KEY, "1");
      setLocation(fallbackHref);
      return;
    }
    try {
      await start();
    } catch (err) {
      toast({ ...checkoutErrorToast(err), variant: "destructive" });
    }
  };

  return (
    <button type="button" onClick={onClick} disabled={isPending} className={className}>
      {isPending ? "Redirecting…" : children}
    </button>
  );
}

export function Pricing() {
  return (
    <section
      id="pricing"
      className="relative scroll-mt-24 overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50 py-24"
    >
      {/* Soft brand glow behind the highlighted plan */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
            Pricing
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Start free. Upgrade when you're ready to chart for your whole clinic.
          </p>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Start with a free 14-day trial. Upgrade only if PalNote saves you time.
          </p>
        </Reveal>

        <div className="mt-20 grid items-stretch gap-6 lg:grid-cols-3 lg:gap-8">
          {plans.map((plan, i) => {
            const featureAccent = plan.highlighted
              ? "bg-blue-600 text-white"
              : "bg-blue-100 text-blue-600";
            const ctaClass = `mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 ${
              plan.highlighted
                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 hover:bg-blue-700"
                : "border border-slate-200 bg-white text-slate-800 hover:border-blue-300 hover:bg-slate-50"
            }`;
            return (
              <Reveal key={plan.name} delay={i * 0.1} className="h-full">
                <div
                  className={`group relative flex h-full flex-col rounded-3xl p-8 transition-all duration-300 ${
                    plan.highlighted
                      ? "border-2 border-blue-600 bg-white shadow-2xl shadow-blue-900/15 lg:-translate-y-4 lg:scale-[1.03]"
                      : "border border-slate-200/80 bg-white/80 shadow-sm backdrop-blur-sm hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl"
                  }`}
                >
                  {plan.highlighted && (
                    <>
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-b from-blue-50/70 to-transparent"
                      />
                      <span className="absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-1.5 text-xs font-semibold text-white shadow-lg shadow-blue-600/30">
                        <Star className="h-3 w-3 fill-current" />
                        Most Popular
                      </span>
                    </>
                  )}

                  <div className="relative">
                    <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>

                    <div className="mt-5 flex items-baseline gap-1.5">
                      <span className="text-4xl font-bold tracking-tight text-slate-900">
                        {plan.price}
                      </span>
                      {plan.period && (
                        <span className="text-sm font-medium text-slate-500">{plan.period}</span>
                      )}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{plan.desc}</p>

                    <ul className="mt-7 space-y-3.5">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${featureAccent}`}
                          >
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          <span className="text-sm text-slate-700">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Spacer pushes the CTA to the bottom so all cards align */}
                  <div className="relative mt-auto pt-8">
                    {plan.external ? (
                      <ContactButton
                        className={`w-full ${ctaClass}`}
                        subject={CLINIC_INQUIRY_SUBJECT}
                        body={CLINIC_INQUIRY_BODY}
                      >
                        {plan.cta}
                      </ContactButton>
                    ) : plan.action === "checkout" ? (
                      <ProCheckoutButton
                        className={`w-full ${ctaClass}`}
                        fallbackHref={plan.href}
                      >
                        {plan.cta}
                      </ProCheckoutButton>
                    ) : (
                      <Link href={plan.href} className={`w-full ${ctaClass}`}>
                        {plan.cta}
                      </Link>
                    )}

                    {plan.note && (
                      <p className="mt-3 text-center text-xs text-slate-500">{plan.note}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-14">
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {guarantees.map((g) => (
              <li key={g} className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <Check className="h-4 w-4 text-blue-600" strokeWidth={3} />
                {g}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
